import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SubjectIcon } from "@/components/SubjectIcon";
import { Trophy, Crown, Medal, Lock, Globe, Sparkle } from "@phosphor-icons/react";

export default function Leaderboard() {
  const [board, setBoard] = useState([]);
  const [game, setGame] = useState(null);
  const [tab, setTab] = useState("xp");
  const [scope, setScope] = useState("all");
  const [national, setNational] = useState(null);

  useEffect(() => {
    api.get("/gamification/leaderboard").then((r) => setBoard(r.data));
    api.get("/gamification/me").then((r) => setGame(r.data));
  }, []);

  useEffect(() => {
    if (tab === "national") api.get(`/ranking/national?scope=${scope}`).then((r) => setNational(r.data));
  }, [tab, scope]);

  const rankColor = (rank) => rank === 1 ? "#FFB800" : rank === 2 ? "#9AA5A0" : rank === 3 ? "#C97B4A" : "#E6E2D8";

  return (
    <div className="space-y-8">
      <div className="animate-fade-up">
        <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Classement & récompenses</h1>
        <p className="mt-1 text-ink/65">Gagnez des XP, montez de niveau et mesurez-vous à tout le Cameroun.</p>
      </div>

      {/* Badges */}
      <div className="rounded-2xl border border-border bg-white p-6">
        <h2 className="font-display text-xl font-bold">Mes badges</h2>
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {game?.badges?.map((b) => (
            <div key={b.id} data-testid={`badge-${b.id}`} className={`rounded-2xl border p-4 text-center transition ${b.earned ? "border-gold bg-gold/5" : "border-border bg-cream opacity-60"}`}>
              <div className={`mx-auto grid h-12 w-12 place-items-center rounded-xl ${b.earned ? "bg-gold/20 text-gold" : "bg-sand text-muted-foreground"}`}>
                {b.earned ? <SubjectIcon name={b.icon} size={26} weight="fill" /> : <Lock size={22} />}
              </div>
              <div className="mt-2 text-sm font-bold">{b.name}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{b.description}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button data-testid="tab-xp" onClick={() => setTab("xp")} className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition ${tab === "xp" ? "bg-terracotta text-white" : "bg-white text-ink/70 border border-border hover:bg-sand"}`}>
          <Trophy weight="fill" size={16} /> Top élèves (XP)
        </button>
        <button data-testid="tab-national" onClick={() => setTab("national")} className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition ${tab === "national" ? "bg-terracotta text-white" : "bg-white text-ink/70 border border-border hover:bg-sand"}`}>
          <Globe weight="fill" size={16} /> Classement national
        </button>
      </div>

      {tab === "xp" && (
        <div className="rounded-2xl border border-border bg-white p-6">
          <div className="flex items-center gap-2"><Trophy weight="fill" className="text-gold" size={22} /><h2 className="font-display text-xl font-bold">Top élèves</h2></div>
          <div className="mt-4 space-y-2">
            {board.map((u) => (
              <div key={u.rank} data-testid={`rank-${u.rank}`} className={`flex items-center gap-4 rounded-xl px-4 py-3 ${u.is_me ? "bg-terracotta/10 ring-1 ring-terracotta" : "bg-cream"}`}>
                <div className="grid h-9 w-9 place-items-center rounded-full font-bold text-ink" style={{ background: rankColor(u.rank) }}>
                  {u.rank <= 3 ? <Crown weight="fill" size={18} /> : u.rank}
                </div>
                <div className="flex-1">
                  <div className="font-semibold">{u.name} {u.is_me && <span className="text-xs text-terracotta">(vous)</span>}</div>
                  <div className="text-xs text-muted-foreground">Niveau {u.level}</div>
                </div>
                <div className="flex items-center gap-1 font-bold text-forest"><Medal weight="fill" size={16} className="text-gold" /> {u.xp} XP</div>
              </div>
            ))}
            {!board.length && <p className="py-6 text-center text-sm text-muted-foreground">Le classement se remplit dès les premiers quiz.</p>}
          </div>
        </div>
      )}

      {tab === "national" && (
        <div className="rounded-2xl border border-border bg-white p-6" data-testid="national-ranking">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2"><Globe weight="fill" className="text-forest" size={22} /><h2 className="font-display text-xl font-bold">Classement national</h2></div>
            <div className="flex gap-2 rounded-full bg-cream p-1 text-xs font-semibold">
              <button data-testid="scope-all" onClick={() => setScope("all")} className={`rounded-full px-3 py-1.5 transition ${scope === "all" ? "bg-white shadow-sm text-terracotta" : "text-muted-foreground"}`}>Tous examens</button>
              <button data-testid="scope-exam" onClick={() => setScope("exam")} className={`rounded-full px-3 py-1.5 transition ${scope === "exam" ? "bg-white shadow-sm text-terracotta" : "text-muted-foreground"}`}>Mon examen</button>
            </div>
          </div>

          {national?.my_rank && (
            <div className="mt-4 flex items-center gap-3 rounded-xl bg-forest px-4 py-3 text-white" data-testid="my-national-rank">
              <Sparkle weight="fill" className="text-gold" size={20} />
              <span className="text-sm font-semibold">Votre rang national : #{national.my_rank} sur {national.total} élèves classés</span>
            </div>
          )}

          <div className="mt-4 space-y-2">
            {national?.top?.map((u) => (
              <div key={u.rank} data-testid={`national-rank-${u.rank}`} className={`flex items-center gap-4 rounded-xl px-4 py-3 ${u.is_me ? "bg-terracotta/10 ring-1 ring-terracotta" : "bg-cream"}`}>
                <div className="grid h-9 w-9 place-items-center rounded-full font-bold text-ink" style={{ background: rankColor(u.rank) }}>
                  {u.rank <= 3 ? <Crown weight="fill" size={18} /> : u.rank}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{u.name} {u.is_me && <span className="text-xs text-terracotta">(vous)</span>}</div>
                  <div className="truncate text-xs text-muted-foreground">{u.exam} · {u.attempts} épreuve(s)</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-forest">{u.avg}%</div>
                  <div className="text-xs text-muted-foreground">moyenne</div>
                </div>
              </div>
            ))}
            {national && !national.top?.length && <p className="py-6 text-center text-sm text-muted-foreground">Passez un examen blanc pour entrer au classement national.</p>}
            {!national && <div className="h-24 animate-pulse rounded-xl bg-sand" />}
          </div>
        </div>
      )}
    </div>
  );
}
